import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { MOCK_ORDERS, MockOrder } from '../mock-data/mock-orders.data';

/**
 * Sticky Columns Example
 * Demonstrates pinned/sticky columns that remain visible during horizontal scroll
 */
@Component({
  selector: 'app-sticky-columns-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Columnas Fijas (Sticky/Pinned)</h2>
        <p class="text-gray-600 mb-2">
          Columnas fijas a la izquierda o derecha que permanecen visibles durante el scroll horizontal.
        </p>
        <div class="flex gap-2 flex-wrap">
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            Pinned Left
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            Pinned Right
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            Horizontal Scroll
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            Shadow Indicators
          </span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          [data]="orders"
          [columnDefs]="columnDefs"
          [config]="gridConfig">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">💡 Características de columnas fijas:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li>La columna "Nro. Documento" está fija a la izquierda (pinned: 'left')</li>
          <li>La columna "Total" está fija a la derecha (pinned: 'right')</li>
          <li>Las columnas fijas permanecen visibles durante el scroll horizontal</li>
          <li>Sombras visuales indican qué columnas están fijas</li>
          <li>Las columnas fijas tienen mayor z-index para estar siempre visibles</li>
          <li>Compatible con selección de filas, ordenamiento y filtros</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">🎯 Casos de uso:</h3>
        <ul class="text-sm space-y-2 list-disc list-inside">
          <li><strong>Identificadores:</strong> Mantén códigos, IDs o nombres siempre visibles</li>
          <li><strong>Acciones:</strong> Fija columnas de acciones (editar, eliminar) a la derecha</li>
          <li><strong>Totales:</strong> Mantén columnas de totales o cantidades siempre visibles</li>
          <li><strong>Tablas anchas:</strong> Ideal para tablas con muchas columnas que requieren scroll horizontal</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900 rounded">
        <h3 class="font-semibold mb-2">⚠️ Nota importante:</h3>
        <p class="text-sm">
          Desplázate horizontalmente para ver cómo las columnas fijas permanecen visibles
          mientras las otras columnas se mueven.
        </p>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📝 Código de este ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{exampleCode}}</code></pre>
      </div>
    </div>
  `,
})
export class StickyColumnsExampleComponent {
  orders: MockOrder[] = MOCK_ORDERS;

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nro. Documento',
      width: 180,
      pinned: 'left',  // ✨ Pin to left side
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 300,
    },
    {
      field: 'class.name',
      headerName: 'Clase',
      width: 200,
    },
    {
      field: 'soldTo.address.city',
      headerName: 'Ciudad',
      width: 180,
    },
    {
      field: 'soldTo.address.street',
      headerName: 'Dirección',
      width: 200,
    },
    {
      field: 'soldTo.address.country',
      headerName: 'País',
      width: 150,
    },
    {
      field: 'subTotal',
      headerName: 'Subtotal',
      width: 130,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'totalTaxes',
      headerName: 'Impuesto',
      width: 130,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'totalDiscounts',
      headerName: 'Descuento',
      width: 130,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'subTotalBeforeTaxes',
      headerName: 'Subtotal antes de impuestos',
      width: 180,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 150,
      pinned: 'right',  // ✨ Pin to right side
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    pagination: {
      enabled: true,
      pageSize: 20,
    },
    sorting: {
      enabled: true,
      multiColumn: true,
    },
    styling: {
      maxHeight: '600px',
      stripedRows: true,
    },
  };

  exampleCode = `// Component Code
columnDefs: ColDef<MockOrder>[] = [
  {
    field: 'serial',
    headerName: 'Nro. Documento',
    width: 180,
    pinned: 'left'  // ✨ Pin column to left
  },
  {
    field: 'soldTo.name',
    headerName: 'Cliente',
    width: 300
    // No pinned - normal scrollable column
  },
  {
    field: 'class.name',
    headerName: 'Clase',
    width: 200
  },
  // ... more scrollable columns
  {
    field: 'total',
    headerName: 'Total',
    width: 150,
    pinned: 'right'  // ✨ Pin column to right
  }
];

// Template
<app-smart-grid
  [data]="orders"
  [columnDefs]="columnDefs"
  [config]="gridConfig">
</app-smart-grid>

// Features:
// - Columns can be pinned to 'left' or 'right'
// - Pinned columns remain visible during horizontal scroll
// - Visual shadows indicate pinned state
// - Works with sorting, filtering, and selection
// - Proper z-index layering (headers > body cells)
// - Multiple columns can be pinned on same side
// - Automatic background coverage for overlapping content`;
}
